import React from 'react';
import { capitalize } from 'lodash';
import styled from 'styled-components';
import { StoreApi, UseBoundStore } from 'zustand';

import { filterKeysToWords } from 'constants/filterKeysConstants';
import { humanize } from 'lib/util';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';
import { DischargeCalendarFilterState } from 'stores/dischargeCalendarStore';
import { InboundReferralsInsightsFilterState } from 'stores/inboundReferralsInsightsStore';
import { InsightsFilterState } from 'stores/insightsStore';
import { PatientFilterState } from 'stores/patientFiltersStore';
import { PortfolioFilterState } from 'stores/portfolioStore';
import { ReferralsInsightsFilterState } from 'stores/referralsInsightsStore';
import { FilterOption, StateWithExpanded } from 'stores/types';
import { colors } from 'styles/theme/colors';
import XIcon from 'svg/XIcon';

import { getFilterCount } from './filters/helpers';
import ChipBag from './ChipBag';

type Props<T extends StateWithExpanded> = {
  filters:
    | InsightsFilterState
    | PatientFilterState
    | PortfolioFilterState
    | DischargeCalendarFilterState
    | ReferralsInsightsFilterState
    | InboundReferralsInsightsFilterState;
  onRemoveFilter?: (filterKey: string, id: string) => void;
  useStore: UseBoundStore<StoreApi<T>>;
};

function getChipLabel(filterKey: string, value: FilterOption | FilterValueBoolean) {
  if (value instanceof FilterValueBoolean) {
    return value.label;
  }

  if (value.id === 'null') {
    return `No ${filterKeysToWords[filterKey] ?? capitalize(humanize(filterKey))} Assigned`;
  }

  return value.name;
}

export default function FilterChips<T extends StateWithExpanded>(props: Props<T>) {
  const { filters, onRemoveFilter, useStore } = props;

  const totalFilters = getFilterCount(filters);

  if (!totalFilters) {
    return <></>;
  }

  const getChips = (filterKey: string, value: Props<T>['filters'][string]) => {
    const chips: React.JSX.Element[] = [];
    if (value instanceof FilterValueBoolean && value.value) {
      chips.push(
        <Chip key={value.name}>
          {getChipLabel(filterKey, value)}
          {onRemoveFilter && (
            <RemoveButton onClick={() => onRemoveFilter(filterKey, '')}>
              <XIcon fill={colors.black100} height={8} width={8} />
            </RemoveButton>
          )}
        </Chip>
      );
    } else if (value instanceof FilterValueArray) {
      value.forEach((val) => {
        chips.push(
          <Chip key={val.id}>
            {getChipLabel(filterKey, val)}
            {onRemoveFilter && (
              <RemoveButton onClick={() => onRemoveFilter(filterKey, val.id as string)}>
                <XIcon fill={colors.black100} height={8} width={8} />
              </RemoveButton>
            )}
          </Chip>
        );
      });
    }

    return chips;
  };

  return (
    <ChipBag useStore={useStore}>
      {Object.entries(filters).map(([filterKey, values]) => getChips(filterKey, values))}
    </ChipBag>
  );
}

export const Chip = styled.div`
  background-color: var(--black-10);
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 24px;
  padding: 4px 12px;

  font-size: 12px;
  font-weight: 500;
  color: var(--black);
  line-height: 16px;
  div {
    cursor: pointer;
  }
`;

export const RemoveButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
